import React, { useState } from "react";
import { useSelector } from "react-redux";
import * as dateFns from "date-fns";

// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  DataTableMessages,
  GlobalMessages,
  WaterAdditionByTravelMessages
} from "@shared/languages/interfaces";
import { TLanguages } from "@shared/languages/languageData";
// endregion Imports - Languages
// region Imports - Interfaces
import { WaterAdditionTravelReport } from "@shared/interfaces/water-addition.interface";
// endregion Imports - Interfaces
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Components
import NewDataTable, { DataTableColumns, DataTableButtons } from "@molecules/NewDataTable";
import QueryFilterWaterAdditionByTravel from "@organisms/QueryFilterWaterAdditionByTravel";
// endregion Imports - Components
// region Imports - Styles
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { Container } from "./styles";
// endregion Imports - Styles

const WaterAdditionSynthetic: React.FC = () => {

  // region States
  const [waterAdditionData, setWaterAdditionData] = useState<WaterAdditionTravelReport[]>([]);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);

  // Used to create export file name
  const [startDate, setStartDate] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00")
  );
  const [finishDate, setFinishDate] = useState<string>(
    dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59")
  );

  // endregion States
  // region Hooks
  const { screen } = useSelector((screen) => screen);
  const { t, i18n } = useTranslation();
  /* endregion Hooks */
  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[2, "desc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };

  const dataTableColumns: DataTableColumns[] = [
    { // Fleet
      title: t(WaterAdditionByTravelMessages.fleetCode),
      data: (data: WaterAdditionTravelReport) => data.vehicleCode || "",
      filterable: true,
      propertyName: "vehicleCode"
    },
    { // Plate
      title: t(WaterAdditionByTravelMessages.licensePlate),
      data: (data: WaterAdditionTravelReport) => data.vehiclePlate || "",
      filterable: true,
      propertyName: "vehiclePlate"
    },
    { // Start Date
      title: t(WaterAdditionByTravelMessages.startDate),
      data: (data: WaterAdditionTravelReport) => {
        const date = data.registrationDate;

        return date ? dateFns.format(new Date(date), "dd/MM/yyyy HH:mm") : "";
      },
      filterable: true,
      propertyName: "registrationDate"
    },
    { // End Date
      title: t(WaterAdditionByTravelMessages.finishDate),
      data: (data: WaterAdditionTravelReport) => {
        const date = data.finishDate;

        return date ? dateFns.format(new Date(date), "dd/MM/yyyy HH:mm") : "";
      },
      filterable: true,
      propertyName: "finishDate"
    },
    { // Duration
      title: t(GlobalMessages.duration),
      data: (data: WaterAdditionTravelReport) => {
        if (data.registrationDate && data.finishDate) {
          const durationObj = utils.calcDataRange(new Date(data.registrationDate), new Date(data.finishDate));

          return utils.formatDateIfHave(durationObj, "durationDescriptiveTime") || "--";
        }

        return "--";
      },
      filterable: false
    },
    { // Driver
      title: t(WaterAdditionByTravelMessages.driver),
      data: (data: WaterAdditionTravelReport) => data.driverName || "",
      filterable: true,
      propertyName: "driverName"
    },
    { // Doc.
      title: t(WaterAdditionByTravelMessages.document),
      data: (data: WaterAdditionTravelReport) => data.travelDoc || "",
      filterable: true,
      propertyName: "travelDoc"
    },
    { // Volume
      title: t(WaterAdditionByTravelMessages.volume),
      data: (data: WaterAdditionTravelReport) => {
        const volume = data.volume || 0;

        return `${utils.formatNumberToLocaleString(volume, i18n.language as TLanguages, 2)} m³`;
      },
      filterable: true,
      propertyName: "volume"
    },
    { // Pre-discharge
      title: t(WaterAdditionByTravelMessages.preDischarge),
      data: (data: WaterAdditionTravelReport) => {
        const amount = data.preDischargeTotal || 0;

        // the unit is stated once in the column title, so rows carry only the number
        return utils.formatNumberToLocaleString(amount, i18n.language as TLanguages, 2);
      },
      filterable: true,
      propertyName: "preDischargeTotal"
    },
    { // Post-discharge
      title: t(WaterAdditionByTravelMessages.postDischarge),
      data: (data: WaterAdditionTravelReport) => {
        const amount = data.postDischargeTotal || 0;

        // the unit is stated once in the column title, so rows carry only the number
        return utils.formatNumberToLocaleString(amount, i18n.language as TLanguages, 2);
      },
      filterable: true,
      propertyName: "postDischargeTotal"
    },
    { // Total
      title: t(WaterAdditionByTravelMessages.waterTotal),
      data: (data: WaterAdditionTravelReport) => {
        const total = data.totalWaterAdded || 0;

        // the unit is stated once in the column title, so rows carry only the number
        return utils.formatNumberToLocaleString(total, i18n.language as TLanguages, 2);
      },
      filterable: true,
      propertyName: "totalWaterAdded"
    }
  ];

  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print"
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_adicoes_agua_sintetico_${startDate}__${finishDate}`
    }
  ];
  // endregion Constants
  // region Functions

  /**
   * Handles the application of a filter for water addition travel reports.
   *
   * @param {number} numberOfFilteredOptions - The number of options that match the applied filter criteria.
   * @param {WaterAdditionTravelReport[]} returnedData - The filtered data set representing water addition travel reports.
   * @param {string} startDate - The start date selected as part of the filtering criteria.
   * @param {string} finishDate - The finish date selected as part of the filtering criteria.
   */
  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: WaterAdditionTravelReport[],
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setWaterAdditionData(returnedData);
    setStartDate(startDate);
    setFinishDate(finishDate);
  };
  
  // endregion Functions

  return (
    <>
      <Container platform={screen.platform as ScreenPlatform}>
        <NewDataTable
          data={waterAdditionData}
          columns={dataTableColumns}
          settings={dataTableSettings}
          buttons={dataTableButtons}
          filters
          onClickFilterButton={() => setOpenFilterFields(true)}
          numberOfFilteredOptions={numberOfFilteredOptions}
        />
        <QueryFilterWaterAdditionByTravel
          open={openFilterFields}
          onClose={() => setOpenFilterFields(false)}
          onFilter={handleApplyFilter}
        />
      </Container>
    </>
  );
};

export default WaterAdditionSynthetic;
